/**
 * FilterService
 *
 * A reusable, object-oriented service for filtering table data.
 * Follows Service-Oriented Design patterns for modularity and reusability.
 *
 * Features:
 * - Multi-criteria filtering (search, dropdowns, etc.)
 * - Case-insensitive search across multiple fields
 * - Real-time or on-demand filtering
 * - Maintains filtered dataset state
 * - Works with any table structure
 */

interface FilterServiceOptions {
    searchFields?: string[];
    filterControls?: Record<string, string>;
}

interface FilterStats {
    total: number;
    filtered: number;
    hidden: number;
    activeFilters: number;
}

type DataItem = Record<string, any>;

/**
 * FilterService class for managing table data filtering
 */
export class FilterService {
    private tableId: string;
    private table: HTMLTableElement | null;
    private tbody: HTMLTableSectionElement | null;
    private allData: DataItem[];
    private filteredData: DataItem[];
    private filterCriteria: Map<string, any>;
    private options: Required<FilterServiceOptions>;

    /**
     * Create a FilterService instance for a specific table
     * @param tableId - The ID of the table element
     * @param options - Configuration options
     */
    constructor(tableId: string, options: FilterServiceOptions = {}) {
        this.tableId = tableId;
        this.table = document.getElementById(tableId) as HTMLTableElement | null;
        this.tbody = null;
        this.allData = [];
        this.filteredData = [];
        this.filterCriteria = new Map<string, any>();

        // Configuration options
        this.options = {
            searchFields: options.searchFields || [],
            filterControls: options.filterControls || {}
        };

        if (!this.table) {
            console.error(`FilterService: Table with ID '${tableId}' not found`);
            return;
        }

        this.tbody = this.table.querySelector('tbody');
        if (!this.tbody) {
            console.error(`FilterService: No tbody found in table '${tableId}'`);
            return;
        }
    }

    /**
     * Initialize the filter service with data and filter controls
     * @param data - The complete dataset to filter
     * @param filterConfig - Configuration for filter controls
     */
    public initialize(data: DataItem[], _filterConfig: Record<string, unknown> = {}): void {
        this.allData = data;
        this.filteredData = [...data];

        console.log(`FilterService initialized for '${this.tableId}' with ${data.length} items`);
    }

    /**
     * Set filter criteria for a specific field
     * @param fieldName - The name of the field to filter
     * @param value - The filter value
     */
    public setFilter(fieldName: string, value: any): void {
        if (value === null || value === undefined || value === '') {
            this.filterCriteria.delete(fieldName);
        } else {
            this.filterCriteria.set(fieldName, value);
        }
    }

    /**
     * Clear all active filters
     */
    public clearFilters(): void {
        this.filterCriteria.clear();
        this.filteredData = [...this.allData];
        console.log(`Cleared all filters for '${this.tableId}'`);
    }

    /**
     * Apply all active filters to the dataset
     * @returns The filtered dataset
     */
    public applyFilters(): DataItem[] {
        console.log(`Applying filters to '${this.tableId}'...`);
        console.log(`   Active filter criteria:`, Array.from(this.filterCriteria.entries()));

        // Start with all data
        this.filteredData = this.allData.filter(item => {
            // Check each filter criterion
            for (const [fieldName, filterValue] of this.filterCriteria.entries()) {
                if (!this.matchesFilter(item, fieldName, filterValue)) {
                    return false;
                }
            }
            return true;
        });

        console.log(`   Filtered ${this.allData.length} items down to ${this.filteredData.length}`);
        return this.filteredData;
    }

    /**
     * Check if an item matches a filter criterion
     * @param item - The data item to check
     * @param fieldName - The field name
     * @param filterValue - The value to match
     * @returns True if the item matches
     */
    private matchesFilter(item: DataItem, fieldName: string, filterValue: any): boolean {
        // Handle search (searches across multiple fields)
        if (fieldName === 'search') {
            return this.matchesSearch(item, filterValue);
        }

        // Handle specific field filters
        const itemValue = this.getFieldValue(item, fieldName);

        if (itemValue === null || itemValue === undefined) {
            return false;
        }

        // Case-insensitive comparison for strings
        const itemValueStr = String(itemValue).toLowerCase();
        const filterValueStr = String(filterValue).toLowerCase();

        return itemValueStr.includes(filterValueStr);
    }

    /**
     * Check if an item matches a search query across multiple fields
     * @param item - The data item to check
     * @param searchQuery - The search query
     * @returns True if the item matches the search
     */
    private matchesSearch(item: DataItem, searchQuery: string): boolean {
        if (!searchQuery) return true;

        const query = searchQuery.toLowerCase();
        const searchFields = this.options.searchFields;

        // Search across all specified fields
        for (const fieldName of searchFields) {
            const fieldValue = this.getFieldValue(item, fieldName);
            if (fieldValue !== null && fieldValue !== undefined) {
                if (String(fieldValue).toLowerCase().includes(query)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Get a field value from an item, supporting nested properties
     * @param item - The data item
     * @param fieldName - The field name (supports dot notation)
     * @returns The field value
     */
    private getFieldValue(item: DataItem, fieldName: string): any {
        // Support dot notation for nested properties (e.g., "metadata.environment")
        const parts = fieldName.split('.');
        let value: any = item;

        for (const part of parts) {
            if (value === null || value === undefined) {
                return null;
            }
            value = value[part];
        }

        return value;
    }

    /**
     * Read filter values from DOM controls and apply them
     * @param controlIds - Map of control IDs to field names
     * @returns The filtered dataset
     */
    public applyFiltersFromControls(controlIds: Record<string, string>): DataItem[] {
        console.log(`Reading filter controls for '${this.tableId}'...`);

        // Clear existing filters
        this.filterCriteria.clear();

        // Read each control and set filter
        for (const [controlId, fieldName] of Object.entries(controlIds)) {
            const control = document.getElementById(controlId) as HTMLInputElement | HTMLSelectElement | null;
            if (control) {
                const value = control.value.trim();
                if (value !== '') {
                    this.setFilter(fieldName, value);
                    console.log(`   Filter set: ${fieldName} = "${value}"`);
                }
            } else {
                console.warn(`   Control '${controlId}' not found`);
            }
        }

        // Apply the filters
        return this.applyFilters();
    }

    /**
     * Get the current filtered dataset
     * @returns The filtered data
     */
    public getFilteredData(): DataItem[] {
        return this.filteredData;
    }

    /**
     * Get filter statistics
     * @returns Statistics about the current filter state
     */
    public getStats(): FilterStats {
        return {
            total: this.allData.length,
            filtered: this.filteredData.length,
            hidden: this.allData.length - this.filteredData.length,
            activeFilters: this.filterCriteria.size
        };
    }

    /**
     * Reset filters and show all data
     */
    public reset(): void {
        this.clearFilters();
        this.filteredData = [...this.allData];
        console.log(`Reset filters for '${this.tableId}'`);
    }
}

// Export to global scope
declare global {
    interface Window {
        FilterService: typeof FilterService;
    }
}

if (typeof window !== 'undefined') {
    window.FilterService = FilterService;
}

console.log('FilterService service loaded');

